import { JSONModel, toJSONContext } from '@/store/models/jsonModel';

export enum DistributorStatus {
  Inactive = 0,
  Active = 1,
}

/** User rows returned on distributor GET (subset of full user; shape may vary by API). */
export interface DistributorUserJSON {
  id?: number;
  name?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
}

export interface DistributorUserRow {
  id: number;
  email: string;
  displayName: string;
  role: string;
}

export interface DistributorJSON {
  id?: number;
  name: string;
  status: number;
  users?: DistributorUserJSON[];
}

function distributorUserFromJSON(raw: DistributorUserJSON): DistributorUserRow | null {
  const id = Number(raw.id);
  if (!Number.isFinite(id) || id < 1) {
    return null;
  }
  const first = raw.first_name != null ? String(raw.first_name).trim() : '';
  const last = raw.last_name != null ? String(raw.last_name).trim() : '';
  const combined = [first, last].filter(Boolean).join(' ').trim();
  const name = raw.name != null ? String(raw.name).trim() : '';
  const displayName = combined || name || String(raw.email || '').trim() || `#${id}`;
  return {
    id,
    email: String(raw.email || ''),
    displayName,
    role: String(raw.role || ''),
  };
}

export default class Distributor extends JSONModel<DistributorJSON> {
  protected _clientProperties = ['users'];

  public id: number = 0;
  public name: string = '';
  public status: DistributorStatus = DistributorStatus.Active;
  /** Populated from API on read; not sent on save (`_clientProperties`). */
  public users: DistributorUserRow[] = [];

  public toJSON(context: toJSONContext = 'server'): DistributorJSON {
    return {
      id: this.id || undefined,
      name: this.name,
      status: this.status,
    };
  }

  public fromJSON(json: DistributorJSON & Record<string, unknown>) {
    if (typeof json.id !== 'undefined') {
      this.id = Number(json.id);
    }
    this.name = String(json.name || '');
    const s = Number(json.status);
    this.status =
      s === DistributorStatus.Inactive || s === DistributorStatus.Active ? s : DistributorStatus.Active;

    const rawUsers = json.users;
    if (rawUsers && Array.isArray(rawUsers)) {
      this.users = rawUsers
        .map((u) => distributorUserFromJSON(u as DistributorUserJSON))
        .filter((row): row is DistributorUserRow => row !== null);
    } else {
      this.users = [];
    }
  }
}
